import { memo, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { DCOLS, FLDS, SEV_STYLE, CONF_COLOR, monoFont, tagSx, selectSx } from './DiConstants';

const PAGE_SIZE = 100;

// ─── FieldCell ────────────────────────────────────────────────────────────────
const FieldCell = memo(function FieldCell({ f }) {
    if (!f?.value) return <TableCell><Box component="span" sx={tagSx('#f3f4f6', '#9ca3af')}>—</Box></TableCell>;
    const c = f.confidence === 'llm' ? CONF_COLOR.llm
        : f.confidence === 'inferred' ? CONF_COLOR.inferred
            : CONF_COLOR.default;
    return <TableCell><Box component="span" sx={tagSx(alpha(c, 0.08), c)}>{f.value}</Box></TableCell>;
});

// ─── IssueRow — keyed by origIdx so memo compares stable references ───────────
const IssueRow = memo(function IssueRow({ r, gi, col }) {
    const ai = FLDS.some(f => r[f]?.confidence === 'llm');
    const sev = SEV_STYLE[r.severity] || SEV_STYLE.Low;
    return (
        <TableRow sx={{ '&:hover td': { backgroundColor: '#fafafa' } }}>
            <TableCell>
                <Typography sx={{ fontFamily: monoFont, fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {r.id}
                </Typography>
            </TableCell>
            <TableCell sx={{ maxWidth: 240 }}>
                <Tooltip title={r.summary} placement="top-start">
                    <Typography sx={{ fontSize: 12, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
                        {r.summary}
                    </Typography>
                </Tooltip>
            </TableCell>
            <FieldCell f={r.module} />
            <FieldCell f={r.platform} />
            <FieldCell f={r.environment} />
            <FieldCell f={r.country} />
            <TableCell><Box component="span" sx={tagSx(sev.bg, sev.color)}>{r.severity}</Box></TableCell>
            <TableCell>
                {col && (
                    <Box component="span" sx={{ fontSize: 10, fontFamily: monoFont, fontWeight: 600, px: '7px', py: '2px', borderRadius: '5px', border: `1px solid ${alpha(col, 0.25)}`, backgroundColor: alpha(col, 0.08), color: col }}>
                        G{gi + 1}
                    </Box>
                )}
            </TableCell>
        </TableRow>
    );
});

// ─── TableTab ─────────────────────────────────────────────────────────────────
export const TableTab = memo(function TableTab({ classified, dupGroups }) {
    const [filters, setFilters] = useState({
        module: 'All', platform: 'All', env: 'All', country: 'All', severity: 'All', dups: false,
    });
    const [page, setPage] = useState(0);

    // Dup index — stable across filter changes
    const dupMap = useMemo(() => {
        const m = {};
        (dupGroups || []).forEach((g, gi) => g.forEach(i => { m[i] = gi; }));
        return m;
    }, [dupGroups]);

    // Filter option lists — built once per dataset
    const opts = useMemo(() => ({
        module: ['All', ...new Set(classified.map(r => r.module?.value).filter(Boolean))].sort(),
        platform: ['All', ...new Set(classified.map(r => r.platform?.value).filter(Boolean))].sort(),
        env: ['All', ...new Set(classified.map(r => r.environment?.value).filter(Boolean))].sort(),
        country: ['All', ...new Set(classified.map(r => r.country?.value).filter(Boolean))].sort(),
        severity: ['All', 'Critical', 'High', 'Medium', 'Low'],
    }), [classified]);

    // Filter across ALL data, preserving original index for dup lookup
    const filtered = useMemo(() => {
        const result = [];
        for (let i = 0; i < classified.length; i++) {
            const r = classified[i];
            if (filters.module !== 'All' && r.module?.value !== filters.module) continue;
            if (filters.platform !== 'All' && r.platform?.value !== filters.platform) continue;
            if (filters.env !== 'All' && r.environment?.value !== filters.env) continue;
            if (filters.country !== 'All' && r.country?.value !== filters.country) continue;
            if (filters.severity !== 'All' && r.severity !== filters.severity) continue;
            if (filters.dups && dupMap[i] === undefined) continue;
            result.push({ r, origIdx: i });
        }
        return result;
    }, [classified, dupMap, filters]);

    // Slice for current page only — 100 rows max in the DOM at once
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageSlice = useMemo(
        () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
        [filtered, page],
    );

    const setF = (key) => (e) => {
        setPage(0); // reset to page 1 on any filter change
        setFilters(f => ({ ...f, [key]: e.target.value }));
    };

    const handleDups = (e) => {
        setPage(0);
        setFilters(f => ({ ...f, dups: e.target.checked }));
    };

    return (
        <Box>
            <Box sx={{ mb: 2.25 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#9ca3af', mb: 0.5 }}>
                    Issues
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.4px', color: '#111827' }}>
                    All classified issues
                </Typography>
            </Box>

            {/* Toolbar */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.75 }}>
                {[
                    { key: 'module', label: 'Module', options: opts.module },
                    { key: 'platform', label: 'Platform', options: opts.platform },
                    { key: 'env', label: 'Env', options: opts.env },
                    { key: 'country', label: 'Country', options: opts.country },
                    { key: 'severity', label: 'Severity', options: opts.severity },
                ].map(({ key, label, options }) => (
                    <FormControl key={key} size="small" sx={selectSx}>
                        <InputLabel sx={{ fontSize: 12 }}>{label}</InputLabel>
                        <Select label={label} value={filters[key]} onChange={setF(key)}>
                            {options.map(o => <MenuItem key={o} value={o} sx={{ fontSize: 12 }}>{o}</MenuItem>)}
                        </Select>
                    </FormControl>
                ))}
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small" checked={filters.dups} onChange={handleDups}
                            sx={{ color: '#d4d8e2', '&.Mui-checked': { color: '#111827' } }}
                        />
                    }
                    label={<Typography sx={{ fontSize: 12, color: '#374151' }}>Duplicates only</Typography>}
                    sx={{ m: 0, border: '1px solid #e8eaef', borderRadius: '8px', px: 1.25, py: 0.875, backgroundColor: '#ffffff' }}
                />
                <Typography sx={{ fontFamily: monoFont, fontSize: 12, color: '#9ca3af', ml: 'auto' }}>
                    {filtered.length} of {classified.length}
                </Typography>
            </Box>

            {/* Table */}
            <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['ID', 'Summary', 'Module', 'Platform', 'Env', 'Country', 'Severity', 'Dup'].map(h => (
                                    <TableCell key={h}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!pageSlice.length ? (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', color: '#9ca3af', py: 5, fontSize: 13 }}>
                                        No issues match the current filters.
                                    </TableCell>
                                </TableRow>
                            ) : pageSlice.map(({ r, origIdx }) => (
                                <IssueRow
                                    key={origIdx}
                                    r={r}
                                    gi={dupMap[origIdx]}
                                    col={dupMap[origIdx] !== undefined ? DCOLS[dupMap[origIdx] % DCOLS.length] : null}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                {/* Pagination footer */}
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 2, py: 1.25, borderTop: '1px solid #e8eaef' }}>
                        <Typography sx={{ fontFamily: monoFont, fontSize: 12, color: '#9ca3af' }}>
                            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
                        </Typography>
                        <IconButton
                            size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}
                            sx={{ color: '#374151', '&.Mui-disabled': { color: '#d4d8e2' } }}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                            sx={{ color: '#374151', '&.Mui-disabled': { color: '#d4d8e2' } }}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </Paper>
        </Box>
    );
});